import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";

import { Box, MenuItem } from "@mui/material";
import JoditEditor, { IJoditEditorProps } from "jodit-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useQuery, useMutation } from "@tanstack/react-query";

import { useAppState } from "../../../../../context/StateContext";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { Customer } from "../../../../../models/customer";
import { Job } from "../../../../../models/job";
import { Sales } from "../../../../../models/sales";
import { NotificationTemplate } from "../../../../../models/notification.template";
import { PushNotificationTarget, Status, UsedInEmailTemplates } from "../../../../../util/enums";
import { templateTags } from "../../../../../constants";
import { handleInsertTemplateTag, addTagsToString } from "../../../../../util/joditEditor";
import { convertHtmlToPlainText } from "../../../../../util/notificationTemplatePreview";
import { Input, Select } from "../../../../Form";
import { FormError } from "../../../../Form/FormError";
import { AppSwitch } from "../../../AppSwitch";
import { NotificationToast } from "../../../Toast/NotificationToast";
import SkeletonComponent from "../../../Skeleton";
import CommunicationCentreService from "../../../../../services/communication.centre.service";
import { EntityContext } from "../../index";
import { getTypeFromEntityContext } from "../../utils";
import {
  PushNotificationEditorContainer,
  SectionTitle,
  ToggleRow,
  ToggleLabel,
  FormRow,
  EditorContainer,
  CharacterCounter,
  CharacterCounterText,
  CharacterCounterHint,
} from "./styles";

interface PushNotificationEditorProps {
  entityContext: EntityContext;
  usedIn?: UsedInEmailTemplates;
  onSend: () => void;
  onCancel: () => void;
}

export interface PushNotificationEditorHandle {
  sendPushNotification: (scheduledDate?: string) => void;
  isSending: boolean;
}

const MAX_CHARACTERS = 178;

const PushNotificationEditor = forwardRef<
  PushNotificationEditorHandle,
  PushNotificationEditorProps
>(({ entityContext, usedIn = UsedInEmailTemplates.ORDERS, onSend, onCancel }, ref) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const textEditorRef = useRef(null);

  const entityId = entityContext.entityId;
  const entity = entityContext.entity;
  const customer = entityContext.customer;

  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate>(
    {} as NotificationTemplate
  );
  const [pushContent, setPushContent] = useState<string>("");
  const [pushSubject, setPushSubject] = useState<string>("");
  const [pushFrom, setPushFrom] = useState<string>("");
  const [pushTo, setPushTo] = useState<number | undefined>(undefined);
  const [pushWebEnabled, setPushWebEnabled] = useState(false);
  const [pushSalesAppEnabled, setPushSalesAppEnabled] = useState(false);
  const [pushOperationsAppEnabled, setPushOperationsAppEnabled] = useState(true);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { data: pushTemplates, isLoading: isPushTemplatesLoading } = useQuery(
    ["communication-centre.templates.push", usedIn, currentUser?.companyId],
    () =>
      CommunicationCentreService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      select: (data) => ({
        ...data,
        data:
          data?.data?.filter((template: NotificationTemplate) =>
            template.notificationTypes?.includes("push")
          ) || [],
      }),
    }
  );

  const { data: availableUsers, isLoading: isLoadingAvailableUsers } = useQuery(
    ["communication-centre.push.available-users", entityId, usedIn],
    () =>
      CommunicationCentreService.getAvailableUsersForPush(
        entityId!,
        getTypeFromEntityContext(entityContext)
      ),
    {
      enabled: !!entityId,
      refetchOnWindowFocus: false,
      select: (response) => response.data,
    }
  );

  const { mutate: sendTemplate, isLoading: isSending } = useMutation(
    ["send.notification.template"],
    async (payload: any) => {
      return await CommunicationCentreService.sendTemplate(payload);
    },
    {
      onSuccess: () => {
        NotificationToast.success(
          translate("communicationSentSuccessfully") || "Communication sent successfully"
        );
        onSend();
      },
      onError: (error: any) => {
        NotificationToast.error(
          error?.response?.data?.message ||
            translate("anErrorOccurredTryAgain") ||
            "An error occurred"
        );
      },
    }
  );

  useEffect(() => {
    if (currentUser?.company?.name) {
      setPushFrom(currentUser.company.name);
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedTemplate.pushContent) {
      setPushContent(addTagsToString(selectedTemplate.pushContent));
    }
    if (selectedTemplate.pushTitle) {
      setPushSubject(selectedTemplate.pushTitle);
    }
  }, [selectedTemplate.id]);

  const characterCount = useMemo(() => {
    const plainText = convertHtmlToPlainText(pushContent);
    return plainText.length;
  }, [pushContent]);

  const config: IJoditEditorProps["config"] = {
    height: "326px",
    toolbarButtonSize: "middle",
    removeButtons: ["fullsize"],
    buttons: ["bold", "italic", "underline", "fontsize", "link", "undo", "redo"],
    controls: {
      customer: {
        name: (translate("customer") as string) || "Customer",
        exec: handleInsertTemplateTag,
        list: templateTags.customer,
      },
      quote: {
        name: (translate("quote") as string) || "Quote",
        exec: handleInsertTemplateTag,
        list: templateTags.quote,
      },
      order: {
        name: (translate("order") as string) || "Order",
        exec: handleInsertTemplateTag,
        list: templateTags.order,
      },
      shipment: {
        name: (translate("shipment") as string) || "Shipment",
        exec: handleInsertTemplateTag,
        list: templateTags.shipment,
      },
    },
    uploader: {
      insertImageAsBase64URI: true,
    },
    extraButtons: ["quote", "order", "shipment", "customer"],
  };

  const schema = yup.object().shape({
    from: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    subject: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    to: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    content: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("character-limit", `Maximum ${MAX_CHARACTERS} characters allowed`, (value) => {
        if (!value) return true;
        const text = value
          .replace(/<[^>]*>/g, "")
          .replace(/<span class="joditTag">.*?<\/span>/g, "")
          .replace(/&nbsp;/g, " ")
          .trim();
        return text.length <= MAX_CHARACTERS;
      }),
    pushNotificationTarget: yup
      .array()
      .min(1, translate("selectAtLeastOneTarget") || "Select at least one target")
      .required(translate("thisFieldIsRequired")),
  });

  const sendPushNotificationHandler = (scheduledDate?: string) => {
    if (!onValidation()) {
      return;
    }

    if (!entityId) {
      NotificationToast.error(translate("anErrorOccurredTryAgain") || "An error occurred");
      return;
    }

    const pushNotificationTargets: string[] = [];
    if (pushWebEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.WEB);
    }
    if (pushSalesAppEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.SALES_APP);
    }
    if (pushOperationsAppEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.OPERATIONS_APP);
    }

    // Start with the selected template (or empty template if none selected)
    const templateData: NotificationTemplate = selectedTemplate.id
      ? { ...selectedTemplate }
      : ({} as NotificationTemplate);

    // Overlay user edits - preserve all template fields, only update edited ones
    const updatedTemplate: NotificationTemplate = {
      ...templateData,
      pushContent: convertHtmlToPlainText(pushContent || templateData.pushContent || ""),
      pushTitle: pushSubject || templateData.pushTitle || "",
      fromName: pushFrom || templateData.fromName || currentUser?.company?.name || "",
      fromEmail: pushFrom || templateData.fromEmail || currentUser?.company?.name || "",
      pushNotificationTarget:
        pushNotificationTargets.length > 0
          ? pushNotificationTargets
          : templateData.pushNotificationTarget || [],
    };

    // Construct the payload for the new endpoint
    // Include the full NotificationTemplate with user edits, plus notificationType and notificationItem
    // Also include push notification-specific fields (to) as they are user edits
    const payload = {
      ...updatedTemplate,
      // Include push notification-specific fields that user may have edited
      to: pushTo,
      notificationType: "push" as const,
      notificationItem: {
        id: entityId,
        type: getTypeFromEntityContext(entityContext),
      },
    };

    sendTemplate(payload);
  };

  const onValidation = () => {
    const pushNotificationTargets: string[] = [];
    if (pushWebEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.WEB);
    }
    if (pushSalesAppEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.SALES_APP);
    }
    if (pushOperationsAppEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.OPERATIONS_APP);
    }

    try {
      schema.validateSync(
        {
          from: pushFrom,
          subject: pushSubject,
          to: pushTo,
          content: pushContent,
          pushNotificationTarget: pushNotificationTargets,
        },
        {
          abortEarly: false,
        }
      );
      setErrors([]);
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    sendPushNotification: (scheduledDate?: string) => sendPushNotificationHandler(scheduledDate),
    isSending,
  }));

  return (
    <PushNotificationEditorContainer>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", padding: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SectionTitle>
              {translate("pushNotificationDetails") || "Push Notification Details"}
            </SectionTitle>
            <ToggleRow sx={{ opacity: 0, pointerEvents: "none" }}>
              <ToggleLabel>{translate("web") || "Web"}</ToggleLabel>
              <AppSwitch
                checked={pushWebEnabled}
                onChange={(e) => setPushWebEnabled(e.target.checked)}
              />
              <ToggleLabel>{translate("salesApp") || "Sales App"}</ToggleLabel>
              <AppSwitch
                checked={pushSalesAppEnabled}
                onChange={(e) => setPushSalesAppEnabled(e.target.checked)}
              />
              <ToggleLabel>{translate("operationsApp") || "Operations App"}</ToggleLabel>
              <AppSwitch
                checked={pushOperationsAppEnabled}
                onChange={(e) => setPushOperationsAppEnabled(e.target.checked)}
              />
            </ToggleRow>
          </Box>

          <FormRow>
            <Box sx={{ flex: 1 }}>
              <SkeletonComponent isLoading={isPushTemplatesLoading} isInput>
                <Select
                  name="template"
                  label={translate("template") || "Template"}
                  value={selectedTemplate?.id || ""}
                  onChange={(event) => {
                    if (Number(event.target.value)) {
                      const template = pushTemplates?.data?.find(
                        (t: NotificationTemplate) => t.id === Number(event.target.value)
                      );
                      if (template) {
                        setSelectedTemplate(template);
                        setPushContent(addTagsToString(template.content || ""));
                        setPushSubject(template.subject || "");
                        if (template.pushNotificationTarget) {
                          setPushWebEnabled(
                            template.pushNotificationTarget.includes(PushNotificationTarget.WEB)
                          );
                          setPushSalesAppEnabled(
                            template.pushNotificationTarget.includes(
                              PushNotificationTarget.SALES_APP
                            )
                          );
                          setPushOperationsAppEnabled(
                            template.pushNotificationTarget.includes(
                              PushNotificationTarget.OPERATIONS_APP
                            )
                          );
                        }
                      }
                    } else {
                      setSelectedTemplate({} as NotificationTemplate);
                    }
                  }}
                  placeholder={translate("selectATemplate") || "Select a template"}
                  fullWidth
                >
                  {pushTemplates?.data?.map((template: NotificationTemplate) => (
                    <MenuItem key={template.name} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input
                label={translate("from") || "From"}
                value={pushFrom}
                onChange={(event) => setPushFrom(event.target.value)}
                disabled
                fullWidth
                required
                error={fieldHasError("from")}
                errorMessage={getErrorByField("from")}
              />
            </Box>
          </FormRow>

          <FormRow>
            <Box sx={{ flex: 1 }}>
              <Input
                label={translate("subject") || "Subject"}
                value={pushSubject}
                onChange={(event) => setPushSubject(event.target.value)}
                placeholder={translate("subject") || "Subject"}
                fullWidth
                required
                error={fieldHasError("subject")}
                errorMessage={getErrorByField("subject")}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SkeletonComponent isLoading={isLoadingAvailableUsers} isInput>
                <Select
                  name="to"
                  label={translate("to") || "To"}
                  value={pushTo || ""}
                  onChange={(event) => setPushTo(Number(event.target.value))}
                  placeholder={translate("selectAnUser") || "Select a user"}
                  fullWidth
                  required
                  error={fieldHasError("to")}
                  errorMessage={getErrorByField("to")}
                >
                  {availableUsers?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Box>
          </FormRow>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <SectionTitle>
            {translate("pushNotificationContent") || "Push Notification Content"}
            <span style={{ color: "#DC3545" }}>{" *"}</span>
          </SectionTitle>
          <FormError isError={!!getErrorByField("content")}>{getErrorByField("content")}</FormError>
          <EditorContainer>
            <JoditEditor
              ref={textEditorRef}
              value={pushContent}
              config={config}
              onBlur={(newContent) => {
                setPushContent(newContent);
              }}
            />
            <CharacterCounter>
              <CharacterCounterText>
                {characterCount} / {MAX_CHARACTERS} {translate("characters") || "characters"}
              </CharacterCounterText>
              <CharacterCounterHint>
                {translate("use178CharactersOrLessForBestDisplay") ||
                  "Use 178 characters or less for best display."}
              </CharacterCounterHint>
            </CharacterCounter>
          </EditorContainer>
        </Box>

      </Box>
    </PushNotificationEditorContainer>
  );
});

PushNotificationEditor.displayName = "PushNotificationEditor";

export default PushNotificationEditor;
